import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { Button } from './components/ui/button';
import EnhancedProjectForm from './components/EnhancedProjectForm';
import EmployeeForm from './components/EmployeeForm';
import CalendarPlanningView from './components/CalendarPlanningView';
import EmployeeList from './components/EmployeeList';
import EmployeeStats from './components/EmployeeStats';
import LeaveManager from './components/LeaveManager';
import TimeEntryForm from './components/TimeEntryForm';
import TimeEntryList from './components/TimeEntryList';
import AllProjectsView from './components/AllProjectsView';
import SettingsDialog from './components/SettingsDialog';
import AccountsDialog from './components/AccountsDialog';
import PurchasesView from './components/PurchasesView';
import { Toaster } from './components/ui/toaster';
import { useToast } from './hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { employeeAPI, projectAPI, pennylaneAPI, authAPI, systemAPI, dataTransformers, handleAPIError, toLocalDateString } from './services/api';
import { getAllAssignedColors } from './utils/colors';
import { countEffectiveLeaveDays } from './utils/workDays';
import { BarChart3, Users, Calendar, CalendarDays, Construction, TrendingUp, Palette, Loader2, Clock, Settings, Receipt, KeyRound, LogOut, Smartphone } from 'lucide-react';

function App({ user, onLoggedOut, onSaisieTerrain }) {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [isEmployeeFormOpen, setIsEmployeeFormOpen] = useState(false);
  const [isProjectFormOpen, setIsProjectFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Chargement des données...');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAccountsOpen, setIsAccountsOpen] = useState(false);
  const [isSyncingPennylane, setIsSyncingPennylane] = useState(false);
  const [pendingQuotes, setPendingQuotes] = useState(null); // null = jamais interrogé ; [] = interrogé, rien à importer
  const [prefillFromQuote, setPrefillFromQuote] = useState(null);
  const [timeEntriesVersion, setTimeEntriesVersion] = useState(0); // +1 à chaque saisie : recharge la liste des pointages
  const [soldUntil, setSoldUntil] = useState(null); // { date, remainingHours, capacityHours } : bandeau du haut
  const [backupAlerts, setBackupAlerts] = useState([]); // messages d'alerte sauvegarde / disque du serveur
  const { toast } = useToast();
  // Garde contre le double-appel de React.StrictMode en dev (l'effet ci-dessous
  // est monté deux fois volontairement) : sans ça, la réconciliation quotidienne
  // (décalages de dates) pourrait s'exécuter deux fois en concurrence.
  const hasRunDailyReconciliation = useRef(false);

  // Alerte sauvegarde : sans elle, une sauvegarde nocturne en échec ne se voyait qu'en allant lire
  // les journaux du serveur. Relue toutes les 30 minutes ; muette si tout va bien (ou hors serveur
  // hébergé : aucun statut). Un échec de lecture n'affiche rien — ce n'est pas une alerte.
  useEffect(() => {
    const formatDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'inconnue');
    const verifier = () => systemAPI.backupStatus().then((s) => {
      const alertes = [];
      if (s.known) {
        if (!s.ok) {
          alertes.push(`La sauvegarde nocturne a échoué (${!s.localOk ? 'sur le serveur' : 'copie hors serveur'}). Dernière réussie : ${formatDate(s.lastSuccessAt)}.`);
        } else if (s.stale) {
          alertes.push(`Aucune sauvegarde nocturne depuis ${Math.round(s.ageHours)} h : le minuteur du serveur ne s'est pas déclenché.`);
        }
        if (s.diskUsedPct >= 90) {
          alertes.push(`Le disque du serveur est plein à ${s.diskUsedPct} % : les sauvegardes risquent de ne plus passer.`);
        }
      }
      setBackupAlerts(alertes);
    }).catch(() => {});
    verifier();
    const timer = setInterval(verifier, 30 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  // « Vendu jusqu'au » : recalculé à chaque changement des chantiers ou de l'équipe
  // (déplacement, congé, nouveau devis...). Un échec laisse simplement « — ».
  useEffect(() => {
    if (isLoading) return;
    projectAPI.soldUntil().then(setSoldUntil).catch(() => setSoldUntil(null));
  }, [projects, employees, isLoading]);

  // Load initial data from API
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        setLoadingMessage('Chargement des employés...');
        
        // Load employees first
        const employeesData = await employeeAPI.getAll();
        const transformedEmployees = employeesData.map(dataTransformers.transformEmployee);
        setEmployees(transformedEmployees);
        
        setLoadingMessage('Chargement des projets...');

        // Load projects
        const projectsData = await projectAPI.getAll();
        const transformedProjects = projectsData.map(dataTransformers.transformProject);
        setProjects(transformedProjects);

        setLoadingMessage('Données chargées avec succès!');

        // Small delay to show success message
        setTimeout(() => {
          setIsLoading(false);
        }, 500);

        // Contrôle quotidien du planning : décale les chantiers non démarrés et
        // corrige les doubles-réservations. Best-effort, ne bloque jamais le chargement.
        if (hasRunDailyReconciliation.current) return;
        hasRunDailyReconciliation.current = true;
        try {
          const reconciliation = await projectAPI.dailyReconciliation();
          if (reconciliation.changed) {
            const refreshedProjects = await projectAPI.getAll();
            setProjects(refreshedProjects.map(dataTransformers.transformProject));

            const parts = [];
            if (reconciliation.delayed?.length > 0) {
              parts.push(`${reconciliation.delayed.length} chantier(s) décalé(s) au lendemain`);
            }
            if (reconciliation.reassigned?.length > 0) {
              parts.push(`${reconciliation.reassigned.length} réassignation(s) pour éviter un double-emploi`);
            }
            if (reconciliation.flaggedConflicts?.length > 0) {
              parts.push(`${reconciliation.flaggedConflicts.length} conflit(s) à vérifier manuellement`);
            }
            if (parts.length > 0) {
              toast({
                title: "Planning réajusté automatiquement",
                description: parts.join(' · '),
                duration: 7000,
              });
            }
          }
        } catch (reconciliationError) {
          console.error('Daily reconciliation failed:', reconciliationError);
        }

      } catch (error) {
        console.error('Failed to load initial data:', error);
        toast({
          title: "Erreur de chargement",
          description: handleAPIError(error, "Impossible de charger les données"),
          variant: "destructive",
          duration: 5000,
        });
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [toast]);

  // Se déconnecter : la session est fermée côté serveur, pas seulement oubliée
  // ici — sinon le cookie resterait valable pour qui reprend l'appareil.
  const handleLogout = async () => {
    try { await authAPI.logout(); } finally { onLoggedOut(); }
  };

  // Recharge tous les chantiers depuis l'API — indispensable depuis que le
  // backend réorganise le planning entier à chaque modification : les dates et
  // équipes des AUTRES chantiers peuvent changer, pas seulement celui qu'on touche.
  const refreshProjects = async () => {
    try {
      const projectsData = await projectAPI.getAll();
      setProjects(projectsData.map(dataTransformers.transformProject));
    } catch (error) {
      console.error('Failed to refresh projects:', error);
    }
  };

  // Helper function to calculate project duration
  const calculateProjectDuration = async (projectData) => {
    try {
      const response = await projectAPI.calculateDuration(projectData);
      return response.duration;
    } catch (error) {
      console.error('Failed to calculate duration:', error);
      // Fallback to simple calculation
      const weeklyCapacity = projectData.minEmployees * 35;
      return Math.max(1, Math.ceil(projectData.hoursToSell / weeklyCapacity));
    }
  };

  // Helper function to auto-assign employees
  const autoAssignEmployees = async (projectData) => {
    try {
      const response = await projectAPI.autoAssign(projectData);
      return response.assignedEmployees;
    } catch (error) {
      console.error('Failed to auto-assign employees:', error);
      return [];
    }
  };

  // Helper function to get next available date
  const getNextAvailableDate = async () => {
    try {
      const response = await projectAPI.getNextAvailableDate();
      return response.nextAvailableDate;
    } catch (error) {
      console.error('Failed to get next available date:', error);
      return new Date().toISOString().split('T')[0];
    }
  };

  const handleCreateProject = async (newProject) => {
    try {
      // Le backend gère maintenant l'assignation automatique lors de la création

      // Recalculer la durée avec les vrais employés assignés (si assignés manuellement)
      if (newProject.assignedEmployees.length > 0) {
        const realDuration = await calculateProjectDuration(newProject);
        newProject.duration = realDuration;
      }

      // Si pas de date imposée, calculer la prochaine date disponible
      if (!newProject.hasFixedStartDate) {
        const nextDate = await getNextAvailableDate();
        newProject.actualStartDate = nextDate;
      } else {
        newProject.actualStartDate = newProject.startDate;
      }

      // Prepare data for API
      const projectForAPI = dataTransformers.prepareProjectForAPI(newProject, false); // false = creation
      
      // Create project via API (backend will handle auto-assignment)
      const createdProject = await projectAPI.create(projectForAPI);
      const transformedProject = dataTransformers.transformProject(createdProject);

      // Le backend a réorganisé tout le planning : recharger tous les chantiers
      await refreshProjects();

      if (transformedProject.pennylaneQuoteId) {
        setPendingQuotes(prev => prev ? prev.filter(q => q.id !== transformedProject.pennylaneQuoteId) : prev);
      }

      toast({
        title: "Chantier créé",
        description: `${transformedProject.name} a été ajouté au planning${transformedProject.assignedEmployees?.length > 0 ? ` avec ${transformedProject.assignedEmployees.length} salarié(s) assigné(s)` : ''}`,
        duration: 3000,
      });

      setIsProjectFormOpen(false);
      setPrefillFromQuote(null);

    } catch (error) {
      console.error('Failed to create project:', error);
      toast({
        title: "Erreur de création",
        description: handleAPIError(error, "Impossible de créer le chantier"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleCreateEmployee = async (newEmployee) => {
    try {
      // Prepare data for API
      const employeeForAPI = dataTransformers.prepareEmployeeForAPI(newEmployee, false); // false = creation
      
      // Create employee via API
      const createdEmployee = await employeeAPI.create(employeeForAPI);
      const transformedEmployee = dataTransformers.transformEmployee(createdEmployee);
      
      setEmployees(prev => [...prev, transformedEmployee]);

      // Nouvelle recrue → le backend a pu redistribuer le travail
      await refreshProjects();

      toast({
        title: "Salarié ajouté",
        description: `${transformedEmployee.fullName} a été ajouté à l'équipe`,
        duration: 3000,
      });

      setIsEmployeeFormOpen(false);
      
    } catch (error) {
      console.error('Failed to create employee:', error);
      toast({
        title: "Erreur de création",
        description: handleAPIError(error, "Impossible d'ajouter le salarié"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };


  // Absence déclarée pour plusieurs salariés : un seul appel, donc une seule
  // réorganisation du planning côté serveur (au lieu d'une par salarié).
  const handleAddBulkAbsence = async (type, assignments, motif) => {
    try {
      const result = await employeeAPI.addBulkAbsence(type, assignments, motif);
      const employeesData = await employeeAPI.getAll();
      setEmployees(employeesData.map(dataTransformers.transformEmployee));
      await refreshProjects();
      const label = type === 'maladie' ? 'Arrêt maladie' : type === 'autre' ? 'Absence' : 'Congé';
      toast({
        title: `${label} enregistré`,
        description: result.updated.length
          ? `${result.totalDays} jour(s) sur ${result.updated.length} salarié(s) : ${result.updated.map(u => u.fullName).join(', ')}`
          : "Aucun jour ajouté (déjà enregistrés)",
        duration: 5000,
      });
    } catch (error) {
      console.error('Failed to add bulk absence:', error);
      toast({
        title: "Erreur",
        description: handleAPIError(error, "Impossible d'enregistrer l'absence"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleUpdateEmployee = async (updatedEmployee) => {
    try {
      // Prepare data for API  
      const employeeForAPI = dataTransformers.prepareEmployeeForAPI(updatedEmployee, true); // true = update
      
      // Update employee via API
      const updatedEmployeeFromAPI = await employeeAPI.update(updatedEmployee.id, employeeForAPI);
      const transformedEmployee = dataTransformers.transformEmployee(updatedEmployeeFromAPI);
      
      setEmployees(prev =>
        prev.map(emp => emp.id === transformedEmployee.id ? transformedEmployee : emp)
      );

      // Congés/jours travaillés modifiés → le backend a réorganisé le planning
      await refreshProjects();

      toast({
        title: "Salarié mis à jour",
        description: `${transformedEmployee.fullName} a été modifié`,
        duration: 3000,
      });
      
      setEditingEmployee(null);
      setIsEmployeeFormOpen(false);
      
    } catch (error) {
      console.error('Failed to update employee:', error);
      toast({
        title: "Erreur de mise à jour",
        description: handleAPIError(error, "Impossible de modifier le salarié"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setIsEmployeeFormOpen(true);
  };

  const handleEmployeeFormClose = (open) => {
    setIsEmployeeFormOpen(open);
    if (!open) {
      setEditingEmployee(null); // Reset editing state when dialog closes
    }
  };

  const handleProjectFormClose = (open) => {
    setIsProjectFormOpen(open);
    if (!open) {
      setEditingProject(null); // Reset editing state when dialog closes
      setPrefillFromQuote(null);
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    try {
      const employee = employees.find(emp => emp.id === employeeId);
      
      // Archivé, pas effacé : sa fiche et ses heures passées restent.
      await employeeAPI.delete(employeeId);

      setEmployees(prev => prev.map(emp => (emp.id === employeeId
        ? { ...emp, active: false, archivedAt: new Date().toISOString() } : emp)));

      // Départ d'un salarié → ses chantiers ont été réaffectés par le backend
      await refreshProjects();

      toast({
        title: "Salarié archivé",
        description: `${employee?.fullName} ne figure plus dans l'équipe ; sa fiche et ses heures sont conservées`,
        duration: 4000,
      });

    } catch (error) {
      console.error('Failed to archive employee:', error);
      toast({
        title: "Archivage impossible",
        description: handleAPIError(error, "Impossible d'archiver le salarié"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleRestoreEmployee = async (employeeId) => {
    try {
      const restored = await employeeAPI.restore(employeeId);
      setEmployees(prev => prev.map(emp => (emp.id === employeeId
        ? dataTransformers.transformEmployee(restored) : emp)));
      await refreshProjects();
      toast({ title: "Salarié réactivé", description: `${restored.fullName} est de retour dans l'équipe`, duration: 3000 });
    } catch (error) {
      toast({
        title: "Réactivation impossible",
        description: handleAPIError(error, "Impossible de réactiver le salarié"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleFetchPennylaneQuotes = async () => {
    setIsSyncingPennylane(true);
    try {
      const result = await pennylaneAPI.fetchPendingQuotes();

      if (!result.configured) {
        toast({
          title: "Pennylane non configuré",
          description: result.reason || "Voir backend/.env (PENNYLANE_API_TOKEN)",
          variant: "destructive",
          duration: 6000,
        });
        setPendingQuotes(null);
        return;
      }

      setPendingQuotes(result.quotes);
      toast({
        title: "Devis Pennylane",
        description: result.quotes.length > 0
          ? `${result.quotes.length} devis accepté(s) à importer`
          : "Aucun nouveau devis accepté à importer",
        duration: 4000,
      });

    } catch (error) {
      console.error('Failed to fetch Pennylane quotes:', error);
      toast({
        title: "Erreur de synchronisation",
        description: handleAPIError(error, "Impossible de contacter Pennylane"),
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSyncingPennylane(false);
    }
  };

  const handleCreateProjectFromQuote = (quote) => {
    setEditingProject(null);
    setPrefillFromQuote(quote);
    setIsProjectFormOpen(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setIsProjectFormOpen(true);
  };

  const handleUpdateProject = async (updatedProject) => {
    try {
      // Recalculer la durée avec les vrais employés assignés
      const realDuration = await calculateProjectDuration(updatedProject);
      updatedProject.duration = realDuration;

      // Prepare data for API
      const projectForAPI = dataTransformers.prepareProjectForAPI(updatedProject, true); // true = update
      
      // Update project via API
      const updatedProjectFromAPI = await projectAPI.update(updatedProject.id, projectForAPI);
      const transformedProject = dataTransformers.transformProject(updatedProjectFromAPI);

      // Le backend a réorganisé tout le planning : recharger tous les chantiers
      await refreshProjects();

      toast({
        title: "Chantier mis à jour",
        description: `${transformedProject.name} a été modifié (${realDuration} semaines)`,
        duration: 3000,
      });
      
      setEditingProject(null);
      setIsProjectFormOpen(false);
      
    } catch (error) {
      console.error('Failed to update project:', error);
      toast({
        title: "Erreur de mise à jour",
        description: handleAPIError(error, "Impossible de modifier le chantier"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      const project = projects.find(p => p.id === projectId);
      
      // Delete project via API
      await projectAPI.delete(projectId);

      // Le backend a réorganisé tout le planning : recharger tous les chantiers
      await refreshProjects();
      
      toast({
        title: "Chantier supprimé",
        description: `${project?.name} a été supprimé`,
        duration: 3000,
      });
      
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast({
        title: "Erreur de suppression",
        description: handleAPIError(error, "Impossible de supprimer le chantier"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleAssignEmployee = async (projectId, employeeId) => {
    try {
      const project = projects.find(p => p.id === projectId);
      if (!project) return;
      
      const assignedEmployees = project.assignedEmployees || [];
      const isAssigned = assignedEmployees.includes(employeeId);
      
      const updatedAssignedEmployees = isAssigned 
        ? assignedEmployees.filter(id => id !== employeeId)
        : [...assignedEmployees, employeeId];
      
      // Update project with new assignments via API
      await projectAPI.update(projectId, {
        assignedEmployees: updatedAssignedEmployees
      });

      // Le backend a réorganisé tout le planning : recharger tous les chantiers
      await refreshProjects();
      
    } catch (error) {
      console.error('Failed to assign employee:', error);
      toast({
        title: "Erreur d'assignation",
        description: handleAPIError(error, "Impossible d'assigner le salarié"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleMoveProject = async (updatedProject) => {
    // The move (including persistence via /projects/move-with-conflict-resolution)
    // already happened in CalendarPlanningView.handleDrop. Le backend a ensuite
    // réorganisé les autres chantiers autour de cette ancre : tout recharger.
    const transformedProject = dataTransformers.transformProject(updatedProject);

    await refreshProjects();

    toast({
      title: "Planning mis à jour",
      description: `${transformedProject.name} a été déplacé`,
      duration: 3000,
    });
  };

  const handleReleaseProject = async (project) => {
    // Retire les ancres manuelles (date posée par glisser-déposer, équipe choisie
    // à la main) : le chantier redevient entièrement flexible et le moteur de
    // réorganisation reprend la main immédiatement.
    try {
      await projectAPI.update(project.id, { manuallyPlaced: false, manualAssignment: false });
      await refreshProjects();
      toast({
        title: "Chantier libéré",
        description: `${project.name} est de nouveau replanifié automatiquement`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to release project:', error);
      toast({
        title: "Erreur",
        description: handleAPIError(error, "Impossible de libérer le chantier"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleCompleteProject = async (updatedProject) => {
    try {
      let updatedProjectFromAPI;
      
      if (updatedProject.status === 'completed') {
        // Mark as completed via API
        updatedProjectFromAPI = await projectAPI.markCompleted(updatedProject.id);
      } else if (updatedProject.status === 'in-progress') {
        // Démarrage (ou reprise) : le 1er jour du chantier devient aujourd'hui,
        // pas la date initialement prévue.
        updatedProjectFromAPI = await projectAPI.update(updatedProject.id, {
          status: 'in-progress',
          actualStartDate: toLocalDateString(new Date()),
          actualEndDate: null
        });
      } else {
        // Reactivate project
        updatedProjectFromAPI = await projectAPI.update(updatedProject.id, {
          status: updatedProject.status,
          actualEndDate: null
        });
      }
      
      const transformedProject = dataTransformers.transformProject(updatedProjectFromAPI);

      // Le backend a réorganisé tout le planning : recharger tous les chantiers
      await refreshProjects();

      const actionText = transformedProject.status === 'completed'
        ? 'terminé'
        : transformedProject.status === 'in-progress'
          ? 'marqué en cours'
          : 'réactivé';
      toast({
        title: `Chantier ${actionText}`,
        description: `${transformedProject.name} a été ${actionText}`,
        duration: 3000,
      });
      
    } catch (error) {
      console.error('Failed to complete project:', error);
      toast({
        title: "Erreur de modification",
        description: handleAPIError(error, "Impossible de modifier le statut du chantier"),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  // Met un chantier en pause : son équipe est démobilisée et recalée sur
  // d'autres chantiers dès la réorganisation qui suit.
  const handlePauseProject = async (project) => {
    try {
      const updated = await projectAPI.pause(project.id);
      await refreshProjects();
      toast({
        title: 'Chantier mis en pause',
        description: `${dataTransformers.transformProject(updated).name} — équipe libérée pour d'autres chantiers`,
        duration: 4000,
      });
    } catch (error) {
      console.error('Failed to pause project:', error);
      toast({
        title: 'Erreur',
        description: handleAPIError(error, 'Impossible de mettre ce chantier en pause'),
        variant: 'destructive',
        duration: 5000,
      });
    }
  };

  // Reprend un chantier en pause : repositionné par le moteur, avec son
  // ancienne équipe si elle est toujours disponible.
  const handleResumeProject = async (project) => {
    try {
      const updated = await projectAPI.resume(project.id);
      await refreshProjects();
      toast({
        title: 'Chantier repris',
        description: `${dataTransformers.transformProject(updated).name} a été replanifié`,
        duration: 4000,
      });
    } catch (error) {
      console.error('Failed to resume project:', error);
      toast({
        title: 'Erreur',
        description: handleAPIError(error, 'Impossible de reprendre ce chantier'),
        variant: 'destructive',
        duration: 5000,
      });
    }
  };

  // Statistiques
  const activeProjects = projects.filter(p => p.status !== 'completed');
  const completedProjects = projects.filter(p => p.status === 'completed');
  // Total des ventes (en heures pondérées) : ce qu'il reste à produire — la totalité
  // des chantiers planifiés, plus le solde des chantiers en cours ou en pause (heures
  // vendues moins heures déjà réellement effectuées). Les terminés ne comptent plus.
  // Même règle que « Vendu jusqu'au » (GET /api/planning/sold-until).
  const totalHours = projects.reduce((sum, p) => {
    if (p.status === 'planned') return sum + (p.hoursToSell || 0);
    if (p.status === 'in-progress' || p.status === 'paused') {
      return sum + Math.max(0, (p.hoursToSell || 0) - (p.actualHoursWorked || 0));
    }
    return sum;
  }, 0);
  const totalProjects = activeProjects.length;
  // Les salariés archivés gardent leur fiche mais ne font plus partie de l'équipe.
  const equipe = employees.filter(emp => !emp.archivedAt);
  const totalEmployees = equipe.length;
  const availableEmployees = equipe.filter(emp => emp.status === 'available' && emp.active !== false).length;

  // Show loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-white p-8 rounded-lg shadow-lg border border-green-200">
            <Loader2 className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-green-800 mb-2">
              Planification de Chantiers Pro
            </h2>
            <p className="text-green-600">
              {loadingMessage}
            </p>
          </div>
        </div>
        <Toaster />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* En-tête */}
      <header className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-3 py-4 sm:py-6">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-green-500 to-blue-500 p-2 rounded-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-green-700 to-blue-700 bg-clip-text text-transparent">
                  Planification de Chantiers Pro
                </h1>
                <p className="hidden sm:block text-green-600 text-sm">
                  Gérez vos projets et équipes avec le diagramme de Gantt avancé
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <div className="hidden sm:flex items-center space-x-6 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">{totalProjects}</div>
                  <div className="text-green-600">Chantiers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-700">{totalEmployees}</div>
                  <div className="text-blue-600">Salariés</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-700">
                    {Math.round(totalHours).toLocaleString('fr-FR')}h
                  </div>
                  <div className="text-green-600">Carnet de commande</div>
                </div>
                <div className="text-center"
                     title={soldUntil?.date
                       ? `Les ${soldUntil.remainingHours.toLocaleString('fr-FR')} h vendues restantes (chantiers planifiés, en cours et en pause) occupent toute l'équipe jusqu'à cette date (capacité pondérée par le rendement et la présence, absences et fériés déduits)`
                       : 'Aucune heure vendue à produire, ou carnet au-delà de trois ans'}>
                  <div className="text-2xl font-bold text-blue-700">
                    {soldUntil?.date
                      ? new Date(`${soldUntil.date}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })
                      : '—'}
                  </div>
                  <div className="text-blue-600">Vendu jusqu'au</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="hidden md:block text-right mr-1">
                  <div className="text-sm font-medium text-gray-700">{user?.displayName}</div>
                  <div className="text-xs text-gray-400">Responsable</div>
                </div>
                <Button
                  variant="outline"
                  onClick={onSaisieTerrain}
                  className="sm:hidden border-green-200 text-green-700 hover:bg-green-50"
                  title="Saisir des heures (écran mobile)"
                  aria-label="Saisir des heures"
                >
                  <Smartphone className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsAccountsOpen(true)}
                  className="border-gray-200 text-gray-700 hover:bg-gray-50"
                  title="Accès à l'application"
                  aria-label="Accès à l'application"
                >
                  <KeyRound className="w-4 h-4" />
                </Button>
                <AccountsDialog employees={employees} isOpen={isAccountsOpen} onOpenChange={setIsAccountsOpen} />
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="border-gray-200 text-gray-700 hover:bg-gray-50"
                  title="Se déconnecter"
                  aria-label="Se déconnecter"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsSettingsOpen(true)}
                  className="border-gray-200 text-gray-700 hover:bg-gray-50"
                  title="Paramètres"
                  aria-label="Paramètres"
                >
                  <Settings className="w-4 h-4" />
                </Button>
                <SettingsDialog isOpen={isSettingsOpen} onOpenChange={setIsSettingsOpen} />
                <EmployeeForm 
                  onCreateEmployee={handleCreateEmployee}
                  editingEmployee={editingEmployee}
                  onUpdateEmployee={handleUpdateEmployee}
                  isOpen={isEmployeeFormOpen}
                  onOpenChange={handleEmployeeFormClose}
                />
                <EnhancedProjectForm
                  onCreateProject={handleCreateProject}
                  employees={employees}
                  editingProject={editingProject}
                  onUpdateProject={handleUpdateProject}
                  isOpen={isProjectFormOpen}
                  onOpenChange={handleProjectFormClose}
                  initialData={prefillFromQuote}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {backupAlerts.length > 0 && (
        <div role="alert" className="bg-red-50 border-b border-red-300 text-red-800 text-sm px-4 py-2 text-center space-y-0.5">
          {backupAlerts.map((message) => <div key={message}>⚠ {message}</div>)}
        </div>
      )}

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="planning" className="w-full">
          <TabsList className="flex w-full h-auto justify-start overflow-x-auto gap-1 mb-6 lg:grid lg:grid-cols-5 lg:justify-center lg:gap-0">
            <TabsTrigger value="planning" className="shrink-0 flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>Planning</span>
            </TabsTrigger>
            <TabsTrigger value="rh" className="shrink-0 flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>RH</span>
            </TabsTrigger>
            <TabsTrigger value="projects" className="shrink-0 flex items-center space-x-2">
              <Construction className="w-4 h-4" />
              <span>Chantiers</span>
            </TabsTrigger>
            <TabsTrigger value="timeEntry" className="shrink-0 flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>Heures</span>
            </TabsTrigger>
            <TabsTrigger value="purchases" className="shrink-0 flex items-center space-x-2">
              <Receipt className="w-4 h-4" />
              <span>Achats</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="planning" className="space-y-6">
            {/* Statistiques rapides sur mobile */}
            <div className="sm:hidden grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg shadow border border-green-200 text-center">
                <div className="text-xl font-bold text-green-700">{totalProjects}</div>
                <div className="text-green-600 text-sm">Chantiers</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border border-blue-200 text-center">
                <div className="text-xl font-bold text-blue-700">{totalEmployees}</div>
                <div className="text-blue-600 text-sm">Salariés</div>
              </div>
            </div>

            {/* Légende */}
            <div className="bg-white p-4 rounded-lg shadow border border-green-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-green-800">Légende</h3>
                <div className="flex items-center space-x-1 text-xs text-green-600">
                  <Palette className="w-3 h-3" />
                  <span>Codes visuels</span>
                </div>
              </div>
              
              {/* Statuts et indicateurs */}
              <div className="flex flex-wrap gap-6 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-400 rounded"></div>
                  <span className="text-green-700">En cours</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-gray-400 rounded"></div>
                  <span className="text-green-700">Terminé</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-700 font-semibold">✓</span>
                  <span className="text-green-700">Jours passés : affectations réelles (pointages)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-red-500">📌</span>
                  <span className="text-green-700">Date imposée</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">Complexe</span>
                  <span className="text-green-700">Chantier complexe</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-blue-600">★</span>
                  <span className="text-green-700">Ouvrier qualifié</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-yellow-600">🎓</span>
                  <span className="text-green-700">Apprenti</span>
                </div>
              </div>
            </div>

            {/* Planning Calendrier */}
            <CalendarPlanningView
              projects={activeProjects}
              allProjects={projects}
              employees={employees}
              onUpdateProject={handleMoveProject}
              onCompleteProject={handleCompleteProject}
              onEditProject={handleEditProject}
              onDeleteProject={handleDeleteProject}
              onReleaseProject={handleReleaseProject}
            />
          </TabsContent>

          <TabsContent value="rh" className="space-y-6">
            <Tabs defaultValue="equipe" className="w-full">
              <TabsList className="flex w-full h-auto justify-start overflow-x-auto gap-1 mb-4 sm:grid sm:grid-cols-2 sm:justify-center sm:gap-0">
                <TabsTrigger value="equipe" className="shrink-0 flex items-center space-x-2">
                  <Users className="w-4 h-4" />
                  <span>Équipe</span>
                </TabsTrigger>
                <TabsTrigger value="conges" className="shrink-0 flex items-center space-x-2">
                  <CalendarDays className="w-4 h-4" />
                  <span>Congés et maladie</span>
                </TabsTrigger>
              </TabsList>

            <TabsContent value="equipe" className="space-y-6">
            {/* Statistiques d'équipe */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow border border-blue-200 text-center">
                <div className="text-2xl font-bold text-blue-700">{totalEmployees}</div>
                <div className="text-blue-600 text-sm">Total salariés</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border border-green-200 text-center">
                <div className="text-2xl font-bold text-green-700">{availableEmployees}</div>
                <div className="text-green-600 text-sm">Disponibles</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border border-blue-200 text-center">
                <div className="text-2xl font-bold text-blue-700">{equipe.filter(emp => emp.isQualified).length}</div>
                <div className="text-blue-600 text-sm">Qualifiés</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border border-yellow-200 text-center">
                <div className="text-2xl font-bold text-yellow-700">{equipe.filter(emp => emp.isApprentice).length}</div>
                <div className="text-yellow-600 text-sm">Apprentis</div>
              </div>
            </div>

            {/* Ce que chacun produit, coûte, et dégage chaque mois */}
            <EmployeeStats />

            {/* Liste des employés */}
            <EmployeeList
              employees={employees}
              onEditEmployee={handleEditEmployee}
              onDeleteEmployee={handleDeleteEmployee}
              onRestoreEmployee={handleRestoreEmployee}
            />
            </TabsContent>

            <TabsContent value="conges" className="space-y-6">
            {/* Statistiques des congés et arrêts maladie — seuls les jours
                théoriquement travaillés comptent (week-ends/fériés exclus) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow border border-blue-200 text-center">
                <div className="text-2xl font-bold text-blue-700">
                  {employees.reduce((sum, emp) => sum + countEffectiveLeaveDays(emp, emp.specificLeaveDates), 0)}
                </div>
                <div className="text-blue-600 text-sm">Congés programmés</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border border-red-200 text-center">
                <div className="text-2xl font-bold text-red-700">
                  {employees.reduce((sum, emp) => sum + countEffectiveLeaveDays(emp, emp.sickLeaveDates), 0)}
                </div>
                <div className="text-red-600 text-sm">Arrêts maladie</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border border-green-200 text-center">
                <div className="text-2xl font-bold text-green-700">
                  {employees.reduce((sum, emp) => sum + Math.max(0, emp.annualLeave - countEffectiveLeaveDays(emp, emp.specificLeaveDates)), 0)}
                </div>
                <div className="text-green-600 text-sm">Jours restants</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border border-orange-200 text-center">
                <div className="text-2xl font-bold text-orange-700">
                  {employees.filter(emp => countEffectiveLeaveDays(emp, emp.specificLeaveDates) > emp.annualLeave).length}
                </div>
                <div className="text-orange-600 text-sm">Dépassements</div>
              </div>
            </div>

            {/* Gestionnaire de congés et arrêts maladie */}
            <LeaveManager 
              employees={employees} 
              onUpdateEmployee={handleUpdateEmployee}
              onAddBulkAbsence={handleAddBulkAbsence}
            />
            </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="projects" className="space-y-6">
            {/* Pilotage + liste des chantiers. L'import des devis Pennylane est
                un bouton de la barre d'outils ; les devis à importer ne
                s'affichent qu'une fois trouvés. */}
            <AllProjectsView
              projects={projects}
              employees={employees}
              onEditProject={handleEditProject}
              onDeleteProject={handleDeleteProject}
              onCompleteProject={handleCompleteProject}
              onPauseProject={handlePauseProject}
              onResumeProject={handleResumeProject}
              pendingQuotes={pendingQuotes}
              isSyncingPennylane={isSyncingPennylane}
              onFetchPennylaneQuotes={handleFetchPennylaneQuotes}
              onCreateProjectFromQuote={handleCreateProjectFromQuote}
              onDismissQuotes={() => setPendingQuotes(null)}
            />
          </TabsContent>

          <TabsContent value="timeEntry" className="space-y-6">
            <TimeEntryForm
              projects={projects}
              employees={employees}
              onSaved={() => { setTimeEntriesVersion((v) => v + 1); refreshProjects(); }}
            />
            <TimeEntryList
              projects={projects}
              employees={employees}
              refreshKey={timeEntriesVersion}
              onChanged={refreshProjects}
            />
          </TabsContent>

          <TabsContent value="purchases" className="space-y-6">
            <PurchasesView />
          </TabsContent>
        </Tabs>
      </main>
      
      <Toaster />
    </div>
  );
}

export default App;